import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid
} from 'recharts';
import {
  TrendingUp, TrendingDown, IndianRupee, ShoppingCart, BadgeDollarSign,
  Package, Users, AlertTriangle, ArrowUpRight, Plus, FileText, RotateCcw,
  Layers, ArrowRightLeft, Warehouse, Loader2, Building2, ChevronDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PIE_COLORS = ['#f97316', '#3b82f6', '#10b981', '#a855f7', '#f59e0b', '#ef4444', '#06b6d4', '#84cc16'];
const STATUS_COLORS = { in_stock: '#10b981', sold: '#ef4444', assigned_to_dc: '#f59e0b', rented: '#a855f7', servicing: '#f59e0b' };

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-';

const KpiCard = ({ label, value, pct, icon: Icon, accent, testId }) => (
  <div data-testid={testId} className="bg-white rounded-sm border border-slate-200 p-5 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 ${accent} rounded-sm flex items-center justify-center`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      {pct != null && (
        <span className={`inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-sm ${pct >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {pct >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {Math.abs(pct)}%
        </span>
      )}
    </div>
    <div className="text-2xl font-bold text-slate-900">{value}</div>
    <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{label}</div>
  </div>
);

const QuickLink = ({ icon: Icon, label, onClick, testId, color = 'bg-slate-800' }) => (
  <button onClick={onClick} data-testid={testId}
    className={`inline-flex items-center gap-2 px-3 py-2 ${color} text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:opacity-90 transition-opacity`}>
    <Icon className="h-3.5 w-3.5" /> {label}
  </button>
);

export const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [activePreset, setActivePreset] = useState('');

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchData = useCallback((companyId, fd, td) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    if (fd) params.set('from_date', fd);
    if (td) params.set('to_date', td);
    const qs = params.toString();
    axios.get(`${BACKEND_URL}/api/dashboard/summary${qs ? `?${qs}` : ''}`, getAuthHeaders())
      .then(res => setData(res.data))
      .catch(() => setError('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(selectedCompany, fromDate, toDate); }, [selectedCompany, fromDate, toDate, fetchData]);

  const applyPreset = (preset) => {
    const now = new Date();
    let fd = '', td = '';
    const pad = n => String(n).padStart(2, '0');
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;

    if (preset === 'this_month') {
      fd = fmt(new Date(now.getFullYear(), now.getMonth(), 1));
      td = fmt(now);
    } else if (preset === 'last_month') {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last  = new Date(now.getFullYear(), now.getMonth(), 0);
      fd = fmt(first); td = fmt(last);
    } else if (preset === 'this_quarter') {
      const qStart = Math.floor(now.getMonth() / 3) * 3;
      fd = fmt(new Date(now.getFullYear(), qStart, 1));
      td = fmt(now);
    } else if (preset === 'last_quarter') {
      const qStart = Math.floor(now.getMonth() / 3) * 3 - 3;
      const y = qStart < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const qs = ((qStart % 12) + 12) % 12;
      fd = fmt(new Date(y, qs, 1));
      td = fmt(new Date(y, qs + 3, 0));
    } else if (preset === 'this_year') {
      fd = fmt(new Date(now.getFullYear(), 0, 1));
      td = fmt(now);
    } else if (preset === 'last_year') {
      fd = fmt(new Date(now.getFullYear() - 1, 0, 1));
      td = fmt(new Date(now.getFullYear() - 1, 11, 31));
    } else if (preset === 'all') {
      fd = ''; td = '';
    }
    setActivePreset(preset);
    setFromDate(fd);
    setToDate(td);
  };

  const clearDates = () => { setFromDate(''); setToDate(''); setActivePreset(''); };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error || !data) return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm">{error || 'No data'}</div>;

  const { kpis, sales_vs_purchases, top_products, inventory_by_company, stock_status, recent_sales, recent_bills, low_stock, companies = [], date_range_active } = data;
  const selectedCompanyName = companies.find(c => c.id === selectedCompany)?.name || '';
  const dateRangeLabel = date_range_active
    ? (fromDate && toDate ? `${fromDate} → ${toDate}` : fromDate ? `From ${fromDate}` : toDate ? `Until ${toDate}` : '')
    : '';

  return (
    <div data-testid="dashboard-page">
      {/* Header + Quick Links */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Dashboard</h1>
          <p className="text-slate-600 mt-1 text-sm">
            {selectedCompanyName
              ? <span>Filtered by <span className="font-semibold text-orange-600">{selectedCompanyName}</span></span>
              : 'Real-time overview of your business'}
            {dateRangeLabel && <span className="ml-2 text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">{dateRangeLabel}</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Company filter */}
          {companies.length > 0 && (
            <div className="relative">
              <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <select
                value={selectedCompany}
                onChange={e => setSelectedCompany(e.target.value)}
                data-testid="company-filter"
                className="pl-8 pr-7 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:border-orange-500 focus:outline-none appearance-none min-w-[170px] font-medium text-slate-700"
              >
                <option value="">All Companies</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            </div>
          )}
          <QuickLink icon={Plus} label="Sale Invoice" testId="ql-sale" color="bg-green-600" onClick={() => navigate('/admin/sale/invoices/new')} />
          <QuickLink icon={Plus} label="Purchase Bill" testId="ql-bill" color="bg-blue-600" onClick={() => navigate('/admin/purchase/bills/new')} />
          <QuickLink icon={FileText} label="Delivery Challan" testId="ql-dc" color="bg-orange-500" onClick={() => navigate('/admin/sale/delivery-challan/new')} />
          <QuickLink icon={ArrowRightLeft} label="Internal Transfer" testId="ql-it" color="bg-purple-600" onClick={() => navigate('/admin/inventory/internal-transfers/new')} />
          <QuickLink icon={Warehouse} label="Inventory" testId="ql-inv" color="bg-slate-700" onClick={() => navigate('/admin/inventory')} />
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white border border-slate-200 rounded-sm px-4 py-3 mb-6 flex flex-wrap items-center gap-3">
        {/* Quick preset buttons */}
        <div className="flex flex-wrap gap-1.5">
          {[
            { key: 'this_month',    label: 'This Month' },
            { key: 'last_month',    label: 'Last Month' },
            { key: 'this_quarter',  label: 'This Quarter' },
            { key: 'last_quarter',  label: 'Last Quarter' },
            { key: 'this_year',     label: 'This Year' },
            { key: 'last_year',     label: 'Last Year' },
          ].map(p => (
            <button key={p.key} onClick={() => applyPreset(p.key)} data-testid={`preset-${p.key}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${
                activePreset === p.key
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'border-slate-200 text-slate-600 hover:border-orange-400 hover:text-orange-600'
              }`}>
              {p.label}
            </button>
          ))}
        </div>

        <div className="h-5 w-px bg-slate-200 hidden md:block" />

        {/* Custom date inputs */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500 font-medium whitespace-nowrap">From</label>
            <input type="date" value={fromDate}
              onChange={e => { setFromDate(e.target.value); setActivePreset(''); }}
              data-testid="date-from"
              className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500 font-medium">To</label>
            <input type="date" value={toDate}
              onChange={e => { setToDate(e.target.value); setActivePreset(''); }}
              data-testid="date-to"
              className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" />
          </div>
          {(fromDate || toDate || activePreset) && (
            <button onClick={clearDates} data-testid="clear-dates"
              className="text-xs text-slate-400 hover:text-red-500 font-medium px-2 py-1.5 hover:bg-red-50 rounded-sm transition-colors">
              Clear
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <KpiCard testId="kpi-sales" label={kpis.sales_label || 'Sales (This Month)'} value={formatINR(kpis.sales_this_month)} pct={kpis.sales_pct_change} icon={BadgeDollarSign} accent="bg-green-500" />
        <KpiCard testId="kpi-purchases" label={kpis.purchases_label || 'Purchases (This Month)'} value={formatINR(kpis.purchases_this_month)} pct={kpis.purchases_pct_change} icon={ShoppingCart} accent="bg-blue-500" />
        <KpiCard testId="kpi-receivables" label="Receivables" value={formatINR(kpis.receivables)} icon={IndianRupee} accent="bg-amber-500" />
        <KpiCard testId="kpi-payables" label="Payables" value={formatINR(kpis.payables)} icon={IndianRupee} accent="bg-rose-500" />
        <KpiCard testId="kpi-stock" label="In-Stock Items" value={(kpis.in_stock_items || 0).toLocaleString()} icon={Package} accent="bg-orange-500" />
        <KpiCard testId="kpi-customers" label="Active Customers" value={(kpis.active_customers || 0).toLocaleString()} icon={Users} accent="bg-purple-500" />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Sales vs Purchases */}
        <div className="bg-white rounded-sm border border-slate-200 p-5 lg:col-span-2" data-testid="chart-sales-vs-purchases">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Sales vs Purchases — Last 6 Months</h3>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={sales_vs_purchases} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
              <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="sales" fill="#10b981" name="Sales" radius={[2, 2, 0, 0]} />
              <Bar dataKey="purchases" fill="#3b82f6" name="Purchases" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Stock by Status (donut) */}
        <div className="bg-white rounded-sm border border-slate-200 p-5" data-testid="chart-stock-status">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Stock by Status</h3>
          {stock_status.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={stock_status} dataKey="count" nameKey="status" innerRadius={50} outerRadius={85} paddingAngle={2}>
                  {stock_status.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.status] || PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n.replace(/_/g, ' ')]} contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => v.replace(/_/g, ' ')} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Top selling products */}
        <div className="bg-white rounded-sm border border-slate-200 p-5 lg:col-span-2" data-testid="chart-top-products">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Top 5 Selling Products — Last 30 Days</h3>
          {top_products.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No sales in the last 30 days</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={top_products} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                <XAxis type="number" stroke="#94a3b8" fontSize={11} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={140} />
                <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} />
                <Bar dataKey="amount" fill="#f97316" name="Amount" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Inventory by company */}
        <div className="bg-white rounded-sm border border-slate-200 p-5" data-testid="chart-inv-by-company">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">In-Stock by Company</h3>
          {inventory_by_company.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">No stock</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={inventory_by_company} dataKey="count" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                  {inventory_by_company.map((entry, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 2, border: '1px solid #e2e8f0', fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Lists row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Sales */}
        <div className="bg-white rounded-sm border border-slate-200" data-testid="list-recent-sales">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Recent Sales</h3>
            <button onClick={() => navigate('/admin/sale/invoices')} className="text-xs text-orange-600 hover:text-orange-700 font-medium inline-flex items-center gap-1">View all <ArrowUpRight className="h-3 w-3" /></button>
          </div>
          {recent_sales.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">No invoices yet</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recent_sales.map(s => (
                <button key={s.id} onClick={() => navigate(`/admin/sale/invoices/${s.id}`)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors text-left">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs font-semibold text-slate-900 truncate">{s.invoice_number || '-'}</div>
                    <div className="text-xs text-slate-500 truncate">{s.customer_name || '-'} · {formatDate(s.date)}</div>
                  </div>
                  <div className="text-sm font-bold text-slate-900 flex-shrink-0 ml-3">{formatINR(s.total)}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Recent Bills */}
        <div className="bg-white rounded-sm border border-slate-200" data-testid="list-recent-bills">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Recent Purchase Bills</h3>
            <button onClick={() => navigate('/admin/purchase/bills')} className="text-xs text-orange-600 hover:text-orange-700 font-medium inline-flex items-center gap-1">View all <ArrowUpRight className="h-3 w-3" /></button>
          </div>
          {recent_bills.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">No bills yet</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recent_bills.map(b => (
                <button key={b.id} onClick={() => navigate(`/admin/purchase/bills/${b.id}`)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors text-left">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs font-semibold text-slate-900 truncate">{b.bill_number || '-'}</div>
                    <div className="text-xs text-slate-500 truncate">{b.vendor_name || '-'} · {formatDate(b.date)}</div>
                  </div>
                  <div className="text-sm font-bold text-slate-900 flex-shrink-0 ml-3">{formatINR(b.total)}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Low Stock Alerts */}
        <div className="bg-white rounded-sm border border-slate-200" data-testid="list-low-stock">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider inline-flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Low Stock
            </h3>
            <button onClick={() => navigate('/admin/inventory')} className="text-xs text-orange-600 hover:text-orange-700 font-medium inline-flex items-center gap-1">View all <ArrowUpRight className="h-3 w-3" /></button>
          </div>
          {low_stock.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">All products well stocked</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {low_stock.map(p => (
                <button key={p.product_id} onClick={() => navigate(`/admin/inventory/${p.product_id}`)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors text-left">
                  <div className="text-xs font-medium text-slate-700 truncate flex-1 mr-3">{p.name}</div>
                  <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase rounded ${p.quantity === 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {p.quantity} in stock
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
